import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';

interface FileObject {
  file_name: string; key: string; url: string;
  size: number; mime_type: string; uploaded_at: string;
}

interface Notice {
  notice_id: string; institute_id: string; branch_id: string;
  batch_id: string | null; broadcast_type: string; sent_to: string[];
  broadcast_to?: string; notice_title: string; notice_body: string;
  files: FileObject[]; created_at: string; created_by: string;
}

interface NoticeDetailsModalProps {
  visible: boolean; noticeId: string | null; onHide: () => void;
}

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  navy:       '#0e225d',
  navyMid:    '#1a3370',
  navyDark:   '#0a1a3d',
  gold:       '#fbb21f',
  goldLight:  '#ffc93c',
  goldDim:    'rgba(251,178,31,0.18)',
  goldBorder: 'rgba(251,178,31,0.35)',
  white:      '#ffffff',
  border:     '#E5E7EB',
  bg:         '#F5F7FA',
  muted:      '#9ca3af',
  sub:        '#6b7280',
  body:       '#1f2937',
};

const labelStyle: React.CSSProperties = {
  margin: '0 0 6px 0', fontSize: '0.68rem', color: '#9ca3af',
  fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px'
};

// Broadcast type badges — navy/gold palette
const getBroadcastBadge = (type: string): { bg: string; color: string } => {
  const map: Record<string, { bg: string; color: string }> = {
    branch:       { bg: C.gold,                          color: C.navy },
    multi_branch: { bg: 'rgba(14,34,93,0.12)',           color: C.navy },
    batch:        { bg: 'rgba(251,178,31,0.18)',          color: '#92580a' },
    individual:   { bg: 'rgba(14,34,93,0.08)',            color: C.navy },
  };
  return map[type] || { bg: C.bg, color: C.sub };
};

const getFileIcon   = (mime: string) => mime.includes('pdf') ? 'pi pi-file-pdf' : mime.includes('word') || mime.includes('document') ? 'pi pi-file-word' : mime.includes('excel') || mime.includes('sheet') ? 'pi pi-file-excel' : mime.includes('image') || mime.includes('jpeg') || mime.includes('jpg') ? 'pi pi-image' : 'pi pi-file';
const getFileColor  = (mime: string) => mime.includes('pdf') ? '#d32f2f' : mime.includes('word') || mime.includes('document') ? '#1976d2' : mime.includes('excel') || mime.includes('sheet') ? '#388e3c' : mime.includes('image') || mime.includes('jpeg') || mime.includes('jpg') ? '#f57c00' : C.navy;
const formatFileSize = (b: number) => { if (!b) return '0 B'; const k = 1024, s = ['B','KB','MB']; const i = Math.floor(Math.log(b) / Math.log(k)); return Math.round((b / Math.pow(k, i)) * 100) / 100 + ' ' + s[i]; };

const NoticeDetailsModal: React.FC<NoticeDetailsModalProps> = ({ visible, noticeId, onHide }) => {
  const [notice, setNotice]   = useState<Notice | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => { if (visible && noticeId) fetchNoticeDetails(); }, [visible, noticeId]);

  const fetchNoticeDetails = async () => {
    if (!noticeId) return;
    try {
      setLoading(true); setError(null);
      const res = await apiClient.get(`/v1/noticeboard/${noticeId}`);
      if (res.data.success) setNotice(res.data.data);
      else setError('Failed to load notice details');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load notice details');
    } finally { setLoading(false); }
  };

  const formatDateTime = (d: string) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };

  const handleClose = () => { setNotice(null); setError(null); onHide(); };

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      style={{ width: '90vw', maxWidth: '900px' }}
      dismissableMask
      draggable={false}
      pt={{
        root: { style: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(14,34,93,0.25)' } },
        header: { style: { display: 'none' } },
        content: { style: { padding: '24px' } }
      }}
    >
      {/* ── Loading ─────────────────────────────────────────────────────── */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px', flexDirection: 'column', gap: '16px' }}>
          <ProgressSpinner style={{ width: '48px', height: '48px' }} strokeWidth="3" animationDuration=".8s" />
          <p style={{ color: C.navy, fontSize: '0.9rem', fontWeight: '500' }}>Loading notice details...</p>
        </div>
      )}

      {/* ── Error ───────────────────────────────────────────────────────── */}
      {!loading && error && (
        <div style={{ padding: '4px' }}>
          <Message severity="error" text={error} style={{ marginBottom: '20px', width: '100%', fontSize: '0.9rem' }} />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <GlobalButton label="Close" variant="outline" onClick={handleClose} />
          </div>
        </div>
      )}

      {/* ── Content ─────────────────────────────────────────────────────── */}
      {!loading && !error && notice && (
        <div>
          {/* Modal Header — navy gradient */}
          <div style={{
            background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 50%, ${C.navyDark} 100%)`,
            borderRadius: '16px', padding: '28px', marginBottom: '20px',
            color: C.white, position: 'relative', overflow: 'hidden'
          }}>
            {/* Orbs */}
            <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '180px', height: '180px', background: C.goldDim, borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: '-30px', left: '20%', width: '100px', height: '100px', background: 'rgba(251,178,31,0.05)', borderRadius: '50%', filter: 'blur(30px)', pointerEvents: 'none' }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
              {/* Badge pill */}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '20px', marginBottom: '12px', fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: C.gold }}>
                <i className="pi pi-bell" style={{ fontSize: '0.7rem' }} />
                Notice Details
              </div>
              <h2 style={{ margin: '0 0 8px 0', fontSize: '1.45rem', fontWeight: '700', color: C.white, lineHeight: '1.3', letterSpacing: '-0.3px' }}>
                {notice.notice_title}
              </h2>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', fontFamily: 'monospace' }}>
                ID: {notice.notice_id}
              </p>
            </div>
          </div>

          {/* Notice Content */}
          <div style={{ background: C.white, borderRadius: '14px', padding: '20px', marginBottom: '16px', border: `1px solid ${C.border}`, boxShadow: '0 2px 8px rgba(14,34,93,0.06)' }}>
            {/* Section header row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', paddingBottom: '14px', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '34px', height: '34px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="pi pi-align-left" style={{ color: C.gold, fontSize: '0.88rem' }} />
                </div>
                <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: C.navy }}>Notice Content</h3>
              </div>

              {/* Broadcast type badge */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={labelStyle}>Broadcast Type:</span>
                <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: '700', textTransform: 'capitalize', background: getBroadcastBadge(notice.broadcast_type).bg, color: getBroadcastBadge(notice.broadcast_type).color, border: notice.broadcast_type === 'branch' ? 'none' : `1px solid ${C.goldBorder}` }}>
                  {notice.broadcast_type.replace('_', ' ')}
                </span>
              </div>
            </div>

            {/* Body text */}
            <div style={{ fontSize: '0.9rem', lineHeight: '1.75', color: C.body, whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
              {notice.notice_body}
            </div>
          </div>

          {/* Attachments */}
          {notice.files && notice.files.length > 0 && (
            <div style={{ background: C.white, borderRadius: '14px', padding: '20px', marginBottom: '16px', border: `1px solid ${C.border}`, boxShadow: '0 2px 8px rgba(14,34,93,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', paddingBottom: '14px', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ width: '34px', height: '34px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="pi pi-paperclip" style={{ color: C.gold, fontSize: '0.88rem' }} />
                </div>
                <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: C.navy }}>
                  Attachments
                  <span style={{ marginLeft: '8px', padding: '2px 8px', borderRadius: '20px', background: C.goldDim, color: '#92580a', fontSize: '0.72rem', fontWeight: '700', border: `1px solid ${C.goldBorder}` }}>
                    {notice.files.length}
                  </span>
                </h3>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {notice.files.map((file, index) => (
                  <div
                    key={index}
                    style={{ padding: '14px 16px', background: C.bg, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: `1px solid ${C.border}`, transition: 'all 0.2s ease' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = C.goldBorder; (e.currentTarget as HTMLDivElement).style.background = 'rgba(251,178,31,0.04)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = C.border; (e.currentTarget as HTMLDivElement).style.background = C.bg; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: 0 }}>
                      {/* File type icon */}
                      <div style={{ width: '40px', height: '40px', background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <i className={getFileIcon(file.mime_type)} style={{ color: getFileColor(file.mime_type), fontSize: '1.3rem' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: '0 0 4px 0', fontSize: '0.85rem', fontWeight: '700', color: C.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {file.file_name}
                        </p>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '0.72rem', color: C.muted, fontWeight: '600' }}>
                          <span>{formatFileSize(file.size)}</span>
                          <span style={{ opacity: 0.5 }}>•</span>
                          <span>{file.mime_type.split('/')[1]?.toUpperCase()}</span>
                        </div>
                      </div>
                    </div>

                    {/* Download button — navy with gold hover */}
                    <button
                      onClick={() => window.open(file.url, '_blank')}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, border: 'none', borderRadius: '8px', color: C.white, fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s ease', flexShrink: 0, boxShadow: '0 2px 6px rgba(14,34,93,0.2)' }}
                      onMouseEnter={e => { e.currentTarget.style.background = C.gold; (e.currentTarget as HTMLButtonElement).style.color = C.navy; e.currentTarget.style.boxShadow = '0 4px 10px rgba(251,178,31,0.35)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`; (e.currentTarget as HTMLButtonElement).style.color = C.white; e.currentTarget.style.boxShadow = '0 2px 6px rgba(14,34,93,0.2)'; }}
                    >
                      <i className="pi pi-download" style={{ fontSize: '0.75rem' }} />
                      Download
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* System Information */}
          <div style={{ background: C.bg, borderRadius: '12px', padding: '14px 18px', marginBottom: '20px', border: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: C.sub, fontWeight: '600' }}>
              <i className="pi pi-calendar" style={{ color: C.gold, fontSize: '0.8rem' }} />
              Created: {formatDateTime(notice.created_at)}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: C.sub, fontWeight: '600' }}>
              <i className="pi pi-user" style={{ color: C.gold, fontSize: '0.8rem' }} />
              By: {notice.created_by}
            </span>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={handleClose}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 28px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, border: 'none', borderRadius: '10px', color: C.white, fontSize: '0.85rem', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 8px rgba(14,34,93,0.2)', transition: 'all 0.2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(14,34,93,0.3)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(14,34,93,0.2)'; }}
            >
              <i className="pi pi-times" style={{ fontSize: '0.85rem' }} />
              Close
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default NoticeDetailsModal;

